import type { SemanticModel } from '@aibi/contracts';
import type { SemanticModelIndex } from '@aibi/semantic-model';
import { createContext, useContext, type Dispatch } from 'react';
import type { FieldCatalog } from '../model/field-catalog';
import type { Rejection } from '../model/rejection';
import type { WorkspaceDocument } from '../model/workspace-document';
import type { WorkspaceCommand } from '../model/workspace-reducer';
import type { WorkspaceAction } from '../model/workspace-store';

export type WorkspaceAudience = 'editor' | 'viewer';

/** Lectura del documento (intencion analitica) y del modelo con el que se valida. */
export interface WorkspaceView {
  readonly document: WorkspaceDocument;
  readonly model: SemanticModel;
  readonly index: SemanticModelIndex;
  readonly catalog: FieldCatalog;
  /** `viewer` en reportes publicados: sin paneles de edicion ni seccion de filtros. */
  readonly audience: WorkspaceAudience;
  readonly canUndo: boolean;
  readonly canRedo: boolean;
  readonly rejection: { readonly sequence: number; readonly value: Rejection } | null;
  readonly lastCreatedCardId: string | null;
}

/** Acciones estables (no cambian entre renders) para no re-renderizar a los consumidores. */
export interface WorkspaceActions {
  readonly execute: (command: WorkspaceCommand) => void;
  readonly undo: () => void;
  readonly redo: () => void;
  readonly dismissRejection: () => void;
  readonly dispatch: Dispatch<WorkspaceAction>;
}

export const WorkspaceViewContext = createContext<WorkspaceView | null>(null);
export const WorkspaceActionsContext = createContext<WorkspaceActions | null>(null);

export function useWorkspace(): WorkspaceView {
  const value = useContext(WorkspaceViewContext);
  if (value === null) throw new Error('useWorkspace requiere <WorkspaceProvider>.');
  return value;
}

export function useWorkspaceActions(): WorkspaceActions {
  const value = useContext(WorkspaceActionsContext);
  if (value === null) throw new Error('useWorkspaceActions requiere <WorkspaceProvider>.');
  return value;
}
